import React, { useState, useEffect } from 'react'
import { useQuery, useLazyQuery } from '@apollo/react-hooks'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { LoadingModal } from '../../components/loading/modal'
import CompleteAccountDetails from '../../components/modals/complete-account-details'
import { getGQLErrorCode, getGQLErrorMsg } from '../../lib/apollo'
import { SUBSCRIPTION_STATUS, MW_ERRORS, VOUCHERS } from '../../constants'
import { STORAGE_KEY_VOUCHER_PREFILLED } from '../../storage-keys'
import ACCOUNT_QUERY_WITH_DIR from '../../../graphql/queries/account-with-directives.gql'
import VOUCHER_WITHOUT_SKU_QUERY from '../../../graphql/queries/voucher-without-sku.gql'
import SUBSCRIPTION_REDEEM_VOUCHER_VIEW from '../../../graphql/queries/subscription-redeem-voucher-view.gql'
import SubscriptionSelection from './components/subscription-selection'
import VoucherNotApplicable from './components/voucher-not-applicable'
import AddVoucherForm from './components/add-voucher-form'
import Payment from './components/payment'
import AddVoucherConfirmation from './components/add-voucher-confirmation'
import RedeemVoucherWarning from './components/redeem-voucher-warning'
import { displayErrorDialog } from '../../actions'

const VIEWS = {
  ADD_VOUCHER_FORM_VIEW: 'add-voucher-form',
  SUBSCRIPTION_SELECTION_VIEW: 'subscription-selection',
  VOUCHER_NOT_APPLICABLE_VIEW: 'voucher-not-applicable',
  REDEEM_VOUCHER_WARNING_VIEW: 'redeem-voucher-warning',
  COMPLETE_ACCOUNT_DETAILS_VIEW: 'complete-account-details',
  PAYMENT_VIEW: 'payment',
  CONFIRMATION_VIEW: 'confirmation'
}

/**
 * AddVoucher flow summary:
 * 1) User starts on VIEWS.ADD_VOUCHER_FORM_VIEW.
 * 2) If user has active subscription:
 *    - Redeem voucher directly against subscription.
 *    - Success with warning => VIEWS.REDEEM_VOUCHER_WARNING_VIEW.
 *    - Success without warning => VIEWS.CONFIRMATION_VIEW.
 *    - Voucher not applicable => VIEWS.VOUCHER_NOT_APPLICABLE_VIEW.
 *    - Other errors => stay on VIEWS.ADD_VOUCHER_FORM_VIEW with error message.
 * 3) If user does not have active subscription:
 *    - Validate voucher first.
 *    - Validation error => stay on VIEWS.ADD_VOUCHER_FORM_VIEW with error message.
 *    - Validation success => VIEWS.SUBSCRIPTION_SELECTION_VIEW.
 *    - Continue => VIEWS.COMPLETE_ACCOUNT_DETAILS_VIEW (if required) or VIEWS.PAYMENT_VIEW.
 *    - Payment success => VIEWS.CONFIRMATION_VIEW.
 */
const AddVoucher = () => {
  // Read and consume any prefilled voucher code from session storage
  const prefilledVoucher = sessionStorage.getItem(STORAGE_KEY_VOUCHER_PREFILLED) || ''
  const [view, setView] = useState(VIEWS.ADD_VOUCHER_FORM_VIEW)
  const [error, setError] = useState('')
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('')
  const [isDefaultProfileIncomplete, setIsDefaultProfileIncomplete] = useState(false)
  const [orderId, setOrderId] = useState('')
  const [userHasActiveSubscription, setUserHasActiveSubscription] = useState(false)
  const dispatch = useDispatch()

  const {
    loading: accountQueryLoading,
    data: accountQueryData,
    error: accountQueryError
  } = useQuery(ACCOUNT_QUERY_WITH_DIR, {
    variables: {
      withCreditcards: true,
      withSubscription: true,
      withProfiles: true
    },
    fetchPolicy: 'network-only'
  })

  const [validateVoucher, {
    loading: validateVoucherQueryLoading,
    data: validateVoucherQueryData,
    error: validateVoucherQueryError
  }] = useLazyQuery(VOUCHER_WITHOUT_SKU_QUERY)

  // Handles redeem flow for users with active subscription:
  // success => confirmation, voucher-not-applicable => dedicated view,
  // other errors => reset voucher field and show error on form.
  const [redeemVoucher, {
    loading: redeemVoucherQueryLoading,
    data: redeemVoucherQueryData,
    error: redeemVoucherQueryError
  }] = useLazyQuery(SUBSCRIPTION_REDEEM_VOUCHER_VIEW, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      // Render the warning view if response type is "SubscriptionVoucherWarnView"
      // Possible warning scenario: there is an existing voucher on the subscription.
      // User can choose to continue redeeming the voucher or cancel and return to the form.
      if (data?.subscriptionRedeemVoucherView.__typename === 'SubscriptionVoucherWarnView') {
        // Render redeem voucher warning view
        setView(VIEWS.REDEEM_VOUCHER_WARNING_VIEW)
        return
      }

      // Render confirmation view
      setView(VIEWS.CONFIRMATION_VIEW)
    },
    onError: (queryError) => {
      const errorCode = getGQLErrorCode(queryError)
      if (errorCode === MW_ERRORS.VOUCHER_NOT_APPLICABLE) {
        // Render voucher is NOT applicable view
        setView(VIEWS.VOUCHER_NOT_APPLICABLE_VIEW)
      }

      // Handle other errors e.g. Invalid Voucher Code
      resetAddVoucherFormWithError(getGQLErrorMsg(queryError))
    }
  })

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      voucher: prefilledVoucher
    },
    mode: 'onChange'
  })

  const resetAddVoucherFormWithError = (errorMessage = '') => {
    reset({ voucher: '' })
    setError(errorMessage)
  }

  // Reads account data and derives two flags used by the journey:
  // - whether user has active subscription
  // - whether default profile requires completion before payment
  useEffect(() => {
    if (!accountQueryLoading && accountQueryData) {
      const defaultProfile = accountQueryData.account.profiles?.find(
        profile => profile.isDefault
      )

      setIsDefaultProfileIncomplete(
        Boolean(
          defaultProfile &&
          (defaultProfile.birthYear === null ||
          defaultProfile.gender === null)
        )
      )

      const userSubscription = accountQueryData.account.subscription
      setUserHasActiveSubscription(
        !(userSubscription.status === SUBSCRIPTION_STATUS.NONE ||
        userSubscription.renewalStatus === SUBSCRIPTION_STATUS.NONE)
      )
    }

    if (accountQueryError) {
      setError(getGQLErrorMsg(accountQueryError))
    }
  }, [accountQueryData, accountQueryLoading, accountQueryError])

  // Handles voucher validation for users without active subscription:
  // success => subscription selection, failure => show error on form.
  useEffect(() => {
    if (!validateVoucherQueryLoading && validateVoucherQueryData) {
      // Show error if voucher is valid but not a subscription voucher
      if (validateVoucherQueryData.voucherWithoutSku.type !== VOUCHERS.types.SVOD) {
        const errorMessage = 'Invalid voucher: only subscription vouchers are accepted.'
        resetAddVoucherFormWithError(errorMessage)
        return
      }

      // Render subscription selection view
      setView(VIEWS.SUBSCRIPTION_SELECTION_VIEW)
    } else if (validateVoucherQueryError) {
      // Handle voucher validation error
      resetAddVoucherFormWithError(getGQLErrorMsg(validateVoucherQueryError))
    }
  }, [validateVoucherQueryData, validateVoucherQueryLoading, validateVoucherQueryError])

  // Add voucher form submit handler:
  // - user has active subscription => redeem directly
  // - no active subscription => validate first
  const onSubmit = async ({ voucher }) => {
    // Clear prefilled voucher from session storage now that the form is submitted
    sessionStorage.removeItem(STORAGE_KEY_VOUCHER_PREFILLED)

    // check if voucher is appliable when user has subscription
    if (userHasActiveSubscription) {
      redeemVoucher({
        variables: {
          voucher,
          warn: true
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
      })
      return
    }

    // Validate voucher if user does not have subscription
    validateVoucher({
      variables: {
        voucherCode: voucher
      },
      fetchPolicy: 'network-only'
    })
  }

  // After confirming subscription:
  // - if profile incomplete => route to complete account details
  // - otherwise => continue to payment
  const continueFromSubscriptionSelection = (subscriptionId) => {
    setSelectedSubscriptionId(subscriptionId)

    if (isDefaultProfileIncomplete) {
      // Render complete account details view
      setView(VIEWS.COMPLETE_ACCOUNT_DETAILS_VIEW)
      return
    }

    // Render payment view
    setView(VIEWS.PAYMENT_VIEW)
  }

  const continueFromRedeemVoucherWarning = () => {
    // Render confirmation view
    setView(VIEWS.CONFIRMATION_VIEW)
  }

  const continueFromCompleteAccountDetails = () => {
    // Render payment view
    setView(VIEWS.PAYMENT_VIEW)
  }

  // Payment completion handler routes to final confirmation view.
  const continueFromPayment = (subscriptionId) => {
    // Render confirmation view
    setOrderId(subscriptionId)
    setView(VIEWS.CONFIRMATION_VIEW)
  }

  // Resets form state: clear voucher code and error
  // Returns user to initial add voucher form.
  const goBackToAddVoucherForm = (errorMessage = '') => {
    const normalizedErrorMessage = typeof errorMessage === 'string' ? errorMessage : ''
    resetAddVoucherFormWithError(normalizedErrorMessage)
    setView(VIEWS.ADD_VOUCHER_FORM_VIEW)
  }

  // Payment error handler shows global error dialog,
  // then returns to subscription selection to retry.
  const onPaymentError = (paymentError) => {
    if (!paymentError) return
    dispatch(displayErrorDialog({ message: getGQLErrorMsg(paymentError) || 'An error occurred during payment. Please try again.' }))
    setView(VIEWS.SUBSCRIPTION_SELECTION_VIEW)
  }

  if (accountQueryLoading || validateVoucherQueryLoading || redeemVoucherQueryLoading) {
    return <LoadingModal />
  }

  // View router for each step in the add voucher flow.
  switch (view) {
    case VIEWS.SUBSCRIPTION_SELECTION_VIEW:
      return (
        <SubscriptionSelection
          voucherDetails={validateVoucherQueryData?.voucherWithoutSku}
          onContinueButtonClick={continueFromSubscriptionSelection}
        />
      )
    case VIEWS.VOUCHER_NOT_APPLICABLE_VIEW:
      return (
        <VoucherNotApplicable
          error={getGQLErrorMsg(redeemVoucherQueryError)}
          goBack={goBackToAddVoucherForm}
        />
      )
    case VIEWS.REDEEM_VOUCHER_WARNING_VIEW:
      return (
        <RedeemVoucherWarning
          warningContent={redeemVoucherQueryData?.subscriptionRedeemVoucherView}
          redeemThisVoucher={continueFromRedeemVoucherWarning}
          onCancelCta={goBackToAddVoucherForm}
        />
      )
    case VIEWS.COMPLETE_ACCOUNT_DETAILS_VIEW:
      return (
        <CompleteAccountDetails
          onCompleteAccountDetails={continueFromCompleteAccountDetails}
        />
      )
    case VIEWS.PAYMENT_VIEW:
      return (
        <Payment
          voucher={getValues('voucher')}
          subscriptionId={selectedSubscriptionId}
          continueNextStep={continueFromPayment}
          onError={onPaymentError}
        />
      )
    case VIEWS.CONFIRMATION_VIEW:
      return (
        <AddVoucherConfirmation
          orderId={orderId}
          onError={goBackToAddVoucherForm}
        />
      )
    default:
      return (
        <AddVoucherForm
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          control={control}
          errors={errors}
          error={error}
          setError={setError}
          isSubmitting={isSubmitting}
        />
      )
  }
}

export default AddVoucher
